import type { WorkflowTraceHandle } from '@toolproof-core/genesis/_contract/_index';

import type { SpaceAdapterRuntimeController } from '@toolproof-core/visualization/adapter/_contract/runtime';

import {
    buildOverlayModel,
    getRuntimeTickIntervalMs,
    reduceDerivationTransitionComplete,
    reduceOverlayIntent,
    reduceRuntimeTick,
} from '@/workflow-runner/interaction/overlay';
import { createEpisodeController, createEpisodeEffectOwner } from '@/workflow-runner/episodePlayback';
import { createModeAugmentationController } from '@/workflow-runner/modeAugmentation';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { Config } from '@/workflow-runner/_contract/config';
import type { RuntimeState } from '@/workflow-runner/_contract/runtime';

export type { Config } from '@/workflow-runner/_contract/config';

const baseRuntimeController = {
    reducePlaybackSuspension: ({ runtimeState, suspended, resumeState }) => ({
        ...runtimeState, isPlaying: suspended ? false : resumeState.isPlaying,
    }),
    createInitialRuntimeState: ({ config }) => {
        const runnerConfig = config.adapterModeConfig;
        return {
            adapterMode: 'runner',
            traceSelection: { ...runnerConfig.traceSelection },
            playbackAdvanceMode: runnerConfig.playback.startPlaying ? 'automatic' : 'manual',
            isPlaying: runnerConfig.playback.startPlaying,
            positionIntervalMs: runnerConfig.playback.positionIntervalMs,
            accumulatedMs: 0,
            pendingReplayMove: null,
        };
    },
    buildOverlayModel,
    reduceOverlayIntent,
    reduceDerivationTransitionComplete,
    getRuntimeTickIntervalMs,
    reduceRuntimeTick,
} satisfies SpaceAdapterRuntimeController<Config, RuntimeState, CosmosDataModel>;
const runtimeController = createEpisodeController(baseRuntimeController);

export const mode = {
    adapterMode: 'runner' as const,
    runtimeController,
    createAugmentationController: createModeAugmentationController,
    createRuntimeEffectOwner: () => createEpisodeEffectOwner(runtimeController),
    createRuntimeStateForTrace: (args: {
        config: Config;
        workflowTraceHandle: WorkflowTraceHandle;
    }): RuntimeState => ({
        ...runtimeController.createInitialRuntimeState({ config: args.config }),
        traceSelection: {
            workflowTraceSelectionKind: 'by-handle',
            workflowTraceHandle: args.workflowTraceHandle,
            replayPositionIndex: 0,
        },
    }),
};
